/**
 * Token-backed utility class names for legacy UI surfaces that have not moved
 * directly to shared primitives yet.
 */

const themeColors = {
    // Primary colors - Blue (matching landing page)
    primary: {
        light: 'bg-primary-soft hover:bg-primary-soft text-primary-dark dark:bg-primary-soft dark:text-primary',
        DEFAULT: 'bg-primary hover:bg-primary-dark text-white dark:bg-primary dark:hover:bg-primary-dark',
        dark: 'bg-primary-dark hover:bg-primary text-white dark:bg-primary-dark dark:hover:bg-primary',
        text: 'text-primary',
    },

    // Secondary colors - Purple (from landing page gradient)
    secondary: {
        light: 'bg-secondary-soft hover:bg-secondary-soft text-secondary-dark dark:bg-secondary-soft dark:text-secondary',
        DEFAULT: 'bg-secondary hover:bg-secondary-dark text-white dark:bg-secondary dark:hover:bg-secondary-dark',
        dark: 'bg-secondary-dark hover:bg-secondary text-white dark:bg-secondary-dark dark:hover:bg-secondary',
    },

    // Background colors (matching landing page)
    background: {
        light: 'bg-background-light',
        DEFAULT: 'bg-background',
        dark: 'bg-background-dark',
    },

    // Surface colors (cards, modals, etc - matching landing page)
    surface: {
        light: 'bg-surface-light',
        DEFAULT: 'bg-surface',
        dark: 'bg-surface-dark',
        secondary: 'bg-surface-secondary',
        error: 'bg-red-50 dark:bg-red-950/30',
    },

    // Text colors (matching landing page)
    text: {
        primary: 'text-foreground',
        secondary: 'text-muted-foreground',
        white: 'text-white',
        disabled: 'text-muted-foreground',
        danger: 'text-red-600 dark:text-red-400',
        error: 'text-red-600 dark:text-red-400'
    },

    // Border colors (matching landing page)
    border: {
        light: 'border-border-light',
        DEFAULT: 'border-border',
        dark: 'border-border-dark',
        error: 'border-red-200 dark:border-red-800',
    },

    // Status colors (keeping consistent with landing page color scheme)
    status: {
        success: {
            light: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-100',
            DEFAULT: 'bg-green-600 text-white dark:bg-green-500',
            dark: 'bg-green-700 text-white dark:bg-green-600',
            bg: 'bg-green-100 dark:bg-green-900/30',
            text: 'text-green-600 dark:text-green-400'
        },
        error: {
            light: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-100',
            DEFAULT: 'bg-red-600 text-white dark:bg-red-500',
            dark: 'bg-red-700 text-white dark:bg-red-600',
            bg: 'bg-red-100 dark:bg-red-900/30',
            border: 'border-red-200 dark:border-red-800',
            text: 'text-red-600 dark:text-red-400'
        },
        warning: {
            light: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-100',
            DEFAULT: 'bg-yellow-500 text-white dark:bg-yellow-500',
            dark: 'bg-yellow-600 text-white dark:bg-yellow-600',
            bg: 'bg-yellow-100 dark:bg-yellow-900/30',
            text: 'text-yellow-600 dark:text-yellow-400',
        },
        info: {
            light: 'bg-primary-soft text-primary-dark dark:bg-primary-soft dark:text-primary',
            DEFAULT: 'bg-primary text-white dark:bg-primary',
            dark: 'bg-primary-dark text-white dark:bg-primary-dark',
            text: 'text-primary',
        },
    },

    // Toast colors
    toast: {
        background: {
            success: 'bg-green-600 dark:bg-green-600',
            error: 'bg-red-600 dark:bg-red-600',
            warning: 'bg-yellow-500 dark:bg-yellow-500',
            info: 'bg-blue-600 dark:bg-blue-600'
        },
        text: {
            DEFAULT: 'text-white dark:text-white',
            action: 'text-white/90 hover:text-white dark:text-white/90 dark:hover:text-white'
        },
        border: {
            success: 'border-0',
            error: 'border-0',
            warning: 'border-0',
            info: 'border-0'
        },
        shadow: 'shadow-lg dark:shadow-lg/20'
    },

    // Highlight colors for items
    highlight: {
        light: 'bg-green-100 hover:bg-green-200 dark:bg-green-900/50 dark:hover:bg-green-900/40',
        DEFAULT: 'bg-green-100 hover:bg-green-200 dark:bg-green-900/50 dark:hover:bg-green-900/40',
        dark: 'bg-green-200 hover:bg-green-300 dark:bg-green-800/50 dark:hover:bg-green-800/50',
    },
    
    // Accent colors for highlighting
    accent: {
        DEFAULT: 'bg-yellow-100 dark:bg-amber-900/30',
        hover: 'bg-yellow-200 dark:bg-amber-900/40',
    },

    // Chart colors
    chart: {
        background: {
            light: 'bg-gray-50/80',
            dark: 'dark:bg-gray-800/50'
        },
        grid: {
            stroke: 'rgba(156, 163, 175, 0.1)'
        },
        text: {
            light: 'text-gray-600',
            dark: 'dark:text-gray-300'
        },
        axis: {
            stroke: '#6B7280',
            fill: '#D1D5DB'
        },
        colors: {
            primary: '#60A5FA',
            success: '#34D399',
            warning: '#FBBF24',
            error: '#F87171'
        },
        // One colour per inventory pipeline stage, shared by the Home pipeline
        // cards and the activity chart legend so a stage is the same colour
        // everywhere it appears.
        pipeline: {
            cataloged: '#60A5FA',
            stowed: '#34D399',
            listed: '#FBBF24'
        },
        series: {
            line: {
                strokeWidth: 2,
                dot: {
                    radius: 4,
                    activeRadius: 8
                }
            },
            bar: {
                radius: [4, 4, 0, 0]
            }
        },
        // Add series-specific colors for light/dark modes
        series1: {
            line: '#60A5FA',
            fill: 'rgba(96, 165, 250, 0.1)',
            dark: {
                line: '#93C5FD',
                fill: 'rgba(147, 197, 253, 0.1)'
            }
        },
        series2: {
            line: '#34D399',
            fill: 'rgba(52, 211, 153, 0.1)',
            dark: {
                line: '#6EE7B7',
                fill: 'rgba(110, 231, 183, 0.1)'
            }
        },
        series3: {
            line: '#FBBF24',
            fill: 'rgba(251, 191, 36, 0.1)',
            dark: {
                line: '#FCD34D',
                fill: 'rgba(252, 211, 77, 0.1)'
            }
        }
    },

    // Icon colors
    icon: {
        primary: 'text-white dark:text-white',
        secondary: 'text-gray-500 dark:text-gray-400',
        disabled: 'text-gray-400 dark:text-gray-600',
        active: 'text-white dark:text-white'
    },

    // Collapsible section colors
    collapsible: {
        header: {
            background: 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500',
            text: 'text-gray-900 dark:text-white',
            icon: 'text-gray-500 dark:text-gray-300'
        },
        content: {
            background: 'bg-gray-50 dark:bg-gray-600/50',
            text: 'text-gray-900 dark:text-white',
            select: {
                background: 'bg-white dark:bg-gray-700',
                text: 'text-gray-900 dark:text-white',
                border: 'border border-gray-300 dark:border-gray-500',
                hover: 'hover:border-blue-500 dark:hover:border-blue-400',
                focus: 'focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400',
                disabled: {
                    background: 'bg-gray-100 dark:bg-gray-800',
                    text: 'text-gray-500 dark:text-gray-400',
                    border: 'border-gray-200 dark:border-gray-600'
                }
            }
        }
    },

    // Accordion table colors
    accordionTable: {
        group: {
            header: {
                background: 'bg-slate-700 hover:bg-slate-600 dark:bg-slate-700 dark:hover:bg-slate-600',
                text: 'text-white dark:text-gray-100',
                subtitle: 'text-slate-400 dark:text-slate-300',
                icon: 'text-gray-300 dark:text-gray-400',
            },
            headerConfirmed: {
                background: 'bg-green-800 dark:bg-green-900',
            },
            body: {
                background: 'bg-white dark:bg-gray-800',
            },
            bodyConfirmed: {
                opacity: 'opacity-50',
            },
        },
        row: {
            base: 'border-b border-gray-100 dark:border-gray-700',
            hover: 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
            changed: 'bg-blue-50 dark:bg-blue-900/20',
            removed: 'bg-red-50 dark:bg-red-900/20 opacity-50',
        },
        badge: {
            base: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
            default: 'text-white',
        },
        search: {
            input: `w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 placeholder-gray-400 dark:placeholder-gray-500`,
        },
        description: {
            container: 'px-4 py-3 bg-slate-100 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700 text-sm leading-relaxed',
            text: 'text-slate-700 dark:text-slate-200',
            label: 'text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400',
        },
    },

    // Input colors
    input: {
        DEFAULT: 'bg-background text-foreground border border-border-light',
        background: 'bg-background',
        text: 'text-foreground',
        border: 'border border-border-light',
        hover: 'hover:border-primary',
        focus: 'focus:ring-4 focus:ring-primary/15 focus:border-primary',
        disabled: {
            background: 'bg-background-light',
            text: 'text-muted-foreground',
            border: 'border-border-light'
        },
        error: {
            background: 'bg-red-50 dark:bg-red-900/10',
            text: 'text-red-900 dark:text-red-200',
            border: 'border-red-500 dark:border-red-500',
            focus: 'focus:ring-4 focus:ring-red-500/15 focus:border-red-500'
        }
    },
};

// Component-specific theme configurations
const components = {
    apiCounter: {
        pill: 'logged-in-header__api-counter flex items-center space-x-2 whitespace-nowrap text-sm bg-surface-secondary text-foreground border border-border-light rounded px-3 py-1 cursor-default',
        label: 'text-foreground',
        muted: 'text-muted-foreground',
        usage: {
            normal: 'text-success',
            warning: 'text-amber-600 dark:text-amber-400',
            critical: 'text-error',
            percentage: 'text-amber-600 dark:text-amber-400 text-xs font-semibold'
        },
        quotaBadge: 'text-error text-xs font-semibold uppercase tracking-wide',
        thresholds: [
            { minPercent: 90, usageKey: 'critical' },
            { minPercent: 75, usageKey: 'warning' },
            { minPercent: 0, usageKey: 'normal' }
        ]
    },

    button: {
        base: 'inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold transition-colors duration-150 focus:outline-none focus:ring-4 focus:ring-primary/15 disabled:cursor-not-allowed disabled:opacity-70',
        variants: {
            primary: themeColors.primary.DEFAULT,
            secondary: themeColors.secondary.DEFAULT,
            outline: 'bg-transparent text-foreground shadow-[inset_0_0_0_1px_var(--border)] hover:bg-background-light dark:text-foreground',
            ghost: 'bg-transparent text-muted-foreground hover:bg-background-light hover:text-foreground dark:text-muted-foreground dark:hover:bg-background-light',
            error: themeColors.status.error.DEFAULT,
            success: themeColors.status.success.DEFAULT,
            warning: themeColors.status.warning.DEFAULT,
            info: themeColors.status.info.DEFAULT,
            remove: 'bg-red-600 hover:bg-red-700 text-white dark:bg-red-700 dark:hover:bg-red-800',
            disabled: 'bg-background-light text-muted-foreground cursor-not-allowed',
            iconButton: `p-1 ${themeColors.text.secondary} hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed`
        },
        sizes: {
            sm: 'px-2 py-1 text-sm',
            md: 'px-4 py-2',
            lg: 'px-5 py-3 text-base',
        },
    },

    // Archive batch button configuration
    archiveBatchButton: {
        button: {
            base: 'w-10 h-10 rounded flex items-center justify-center',
            active: themeColors.primary.DEFAULT,
            archived: themeColors.surface.dark
        },
        icon: {
            transform: 'scale(3)',
            color: '#FFFFFF',
            display: 'block'
        }
    },

    // Add form configuration
    form: {
        checkbox: 'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:text-blue-500 dark:focus:ring-blue-400',
    },

    // Input Box Icon configuration for filter inputs
    inputBoxIcon: {
        base: 'px-1.5 py-0 text-xs rounded flex items-center justify-center w-7 h-6',
        variants: {
            default: 'bg-gray-200 text-gray-700 dark:bg-gray-200 dark:text-gray-700',
            active: 'bg-blue-500 text-white dark:bg-blue-500 dark:text-white',
            save: 'bg-gray-200 text-gray-700 dark:bg-gray-200 dark:text-gray-700'
        },
        hover: 'hover:opacity-80 transition-opacity',
        icon: {
            size: 'h-4.5 w-4.5',
            color: 'text-current'
        }
    },

    card: {
        base: `${themeColors.surface.DEFAULT} ${themeColors.text.primary} border border-border-light shadow-card rounded-[16px] dark:bg-surface`,
        header: `px-5 py-4 border-b border-border-light`,
        content: `px-5 py-5 ${themeColors.text.primary}`,
    },

    input: {
        base: `block w-full px-3 py-2 border border-border-light ${themeColors.input.background} ${themeColors.input.text} rounded-[10px] placeholder:text-muted-foreground focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary dark:bg-background dark:text-foreground`,
        error: 'border-red-500 bg-red-50 text-red-900 focus:ring-4 focus:ring-red-500/15 focus:border-red-500 dark:bg-red-900/10 dark:text-red-200',
        disabled: 'bg-background-light text-muted-foreground cursor-not-allowed',
    },

    select: {
        base: `block w-full px-3 py-2 border ${themeColors.border.DEFAULT} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400`,
        background: 'bg-white dark:bg-gray-800',
        text: 'text-gray-900 dark:text-white',
        border: 'border-gray-300 dark:border-gray-700',
        hover: 'hover:border-blue-500 dark:hover:border-blue-400',
        focus: 'focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400',
        disabled: {
            background: 'bg-gray-100 dark:bg-gray-900',
            text: 'text-gray-500 dark:text-gray-400',
            error: 'text-red-600 dark:text-red-400'
        },
        icon: {
            base: 'absolute inset-y-0 right-0 flex items-center pr-3',
            color: 'text-gray-400 dark:text-gray-500',
            error: 'text-red-500 dark:text-red-400'
        },
        container: {
            base: 'relative',
            border: 'border-gray-200 dark:border-gray-800'
        }
    },

    checkbox: {
        base: 'h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:text-blue-500 dark:focus:ring-blue-400',
        table: 'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:text-blue-500 dark:focus:ring-blue-400 cursor-pointer transition-all duration-150',
    },

    toast: {
        base: 'rounded-lg pl-4 pr-4 pt-4 pb-4',
        variants: {
            success: `${themeColors.toast.background.success} ${themeColors.toast.text.DEFAULT} ${themeColors.toast.border.success} ${themeColors.toast.shadow}`,
            error: `${themeColors.toast.background.error} ${themeColors.toast.text.DEFAULT} ${themeColors.toast.border.error} ${themeColors.toast.shadow}`,
            warning: `${themeColors.toast.background.warning} ${themeColors.toast.text.DEFAULT} ${themeColors.toast.border.warning} ${themeColors.toast.shadow}`,
            info: `${themeColors.toast.background.info} ${themeColors.toast.text.DEFAULT} ${themeColors.toast.border.info} ${themeColors.toast.shadow}`,
        },
        action: themeColors.toast.text.action
    },

    // Accordion table component configuration
    accordionTable: {
        container: 'space-y-3',
        toolbar: 'sticky top-0 z-20 flex items-center gap-3 flex-wrap p-3 rounded-lg shadow-sm bg-white dark:bg-gray-800',
    },

    // Table component configuration with required props
    table: {
        base: 'w-full text-lg font-semibold',
        container: 'flex flex-col h-full w-full min-w-0 overflow-hidden',
        // Both axes scroll: on phones every column stays visible and the
        // table scrolls sideways instead of hiding columns.
        scrollContainer: 'overflow-auto flex-grow-1 relative',
        element: 'w-full table-auto sm:table-fixed border-separate border-spacing-0 relative',
        elementDivideY: 'border-collapse',
        header: {
            base: 'sticky top-0 z-10',
            row: 'bg-gray-50 dark:bg-gray-700',
            cell: {
                base: 'text-center font-bold text-sm font-semibold uppercase tracking-wider align-middle border-b-2 relative text-gray-700 dark:text-gray-300',
                text: 'text-gray-700 dark:text-gray-300',
                hover: 'hover:text-gray-900 dark:hover:text-white',
                sortable: 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-600',
                density: {
                    normal: '',
                    compact: '!text-sm',
                    tight: '!text-xs'
                },
                responsive: {
                    actions: 'whitespace-nowrap', // Shrink to content, never wrap
                    image: 'whitespace-nowrap',   // Shrink to content, never wrap
                    text: '',                     // Natural width, allow wrapping when needed
                    textWide: '',                 // Natural width, allow wrapping when needed
                    number: 'whitespace-nowrap',  // Shrink to content, never wrap
                    select: 'whitespace-nowrap',  // Shrink to content, never wrap
                    boolean: 'whitespace-nowrap',
                    currency: 'whitespace-nowrap',
                    date: 'whitespace-nowrap',
                    datetime: 'whitespace-nowrap'
                }
            },
            filter: {
                container: 'flex justify-center mt-1',
                input: {
                    base: `block w-full px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm ${themeColors.input.background} ${themeColors.input.text} ${themeColors.input.border} rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400`,
                    hover: '',
                    focus: '',
                    disabled: '',
                },
            },
        },
        body: {
            base: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
            row: {
                base: 'transition-colors border-b border-gray-200 dark:border-gray-700',
                background: {
                    default: 'bg-white dark:bg-gray-800',
                    hover: 'hover:bg-gray-50 dark:hover:bg-gray-700',
                    selected: 'bg-blue-100 dark:bg-blue-700',
                    disabled: 'opacity-50 cursor-not-allowed',
                },
                cursor: 'cursor-pointer',
                cell: {
                    base: 'align-middle border-b text-center text-sm md:text-lg whitespace-normal break-words hyphens-auto overflow-visible',
                    actions: 'align-middle border-b text-center text-sm md:text-lg whitespace-normal break-words hyphens-auto overflow-visible', // Reduced padding for actions column
                    limitedHeight: 'max-h-16 md:max-h-20 overflow-hidden line-clamp-2 md:line-clamp-3',
                    error: 'text-red-500 dark:text-red-400',
                    alignLeft: 'text-left',
                    alignCenter: 'text-center',
                    alignRight: 'text-right',
                    editable: 'cursor-pointer relative',
                    input: 'w-full p-1 !bg-surface text-foreground border border-primary rounded shadow-sm text-center text-sm md:text-base focus:outline-none focus:border-primary-dark focus:ring-2 focus:ring-primary/25'
                }
            },
            loading: {
                container: 'flex justify-center items-center p-4 flex-grow',
                text: 'text-gray-500 dark:text-gray-400'
            },
            noData: {
                container: 'flex justify-center items-center p-4 flex-grow h-full',
                text: 'text-center text-gray-500 dark:text-gray-400',
                title: 'text-xl md:text-2xl font-bold',
                message: 'text-sm md:text-base'
            },
            emptyMessage: 'p-4 md:p-6 text-center text-sm md:text-base text-gray-500 dark:text-gray-400 h-24 md:h-32 align-middle'
        },
        footer: {
            base: 'px-6 py-4',
            background: 'bg-white dark:bg-gray-800',
        },
        form: {
            checkbox: 'h-4 w-4 text-blue-500 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400 transition-all cursor-pointer'
        },
        globalFilter: {
            container: 'mb-4',
            input: {
                base: 'w-full px-4 py-2 rounded text-center',
                background: 'bg-white dark:bg-gray-800',
                text: 'text-gray-900 dark:text-gray-100',
                border: 'border border-gray-300 dark:border-gray-600',
                focus: 'focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400',
                placeholder: 'placeholder-gray-400 dark:placeholder-gray-500',
            },
        },
        sort: {
            icon: {
                base: 'ml-1 inline-block align-middle',
                asc: '🔼',
                desc: '🔽',
                default: 'text-gray-300 dark:text-gray-600'
            },
        },
        empty: {
            base: 'px-6 py-8 text-center',
            text: 'text-gray-500 dark:text-gray-400',
        },
        loading: {
            base: 'px-6 py-8 text-center',
            text: 'text-gray-500 dark:text-gray-400',
        },
        actions: {
            container: 'relative',
            menu: {
                base: 'absolute mt-2 min-w-[200px] bg-white dark:bg-gray-800 rounded-md shadow-lg z-50',
                inner: 'py-1',
            },
            item: {
                base: 'relative',
            },
            button: {
                base: 'block w-full text-left px-4 py-2 text-sm text-gray-600 dark:text-gray-300',
                hover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
                disabled: 'opacity-50 cursor-not-allowed',
            },
            submenuIndicator: 'float-right',
            submenu: {
                base: 'absolute left-full top-0 -ml-1 min-w-[200px] bg-white dark:bg-gray-800 rounded-md shadow-lg z-[60]',
                left: 'right-full left-auto -mr-1 ml-0',
                inner: 'py-1',
            },
            option: {
                base: 'block w-full text-left px-4 py-2 text-sm text-gray-600 dark:text-gray-300',
                hover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
                disabled: 'opacity-50 cursor-not-allowed',
            },
        },
        pagination: {
            base: 'flex justify-between items-center px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
            pageSizeContainer: 'flex items-center gap-2',
            pageSizeLabel: 'text-sm text-gray-500 dark:text-gray-400',
            pageSizeSelect: 'py-1 px-2 text-sm w-16 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
            info: {
                base: 'text-sm text-gray-500 dark:text-gray-400',
                current: 'text-gray-900 dark:text-gray-100',
            },
            controls: 'flex items-center gap-1',
            button: {
                base: 'p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
                hover: 'hover:bg-gray-50 dark:hover:bg-gray-600',
                disabled: 'opacity-50 cursor-not-allowed',
            },
        },
        utility: {
            truncate: 'overflow-hidden text-ellipsis whitespace-nowrap',
            cursorPointer: 'cursor-pointer',
            cursorNotAllowed: 'cursor-not-allowed',
            tooltip: 'before:content-[attr(title)] before:absolute before:-top-8 before:left-0 before:bg-black/80 before:text-white before:py-1 before:px-2 before:rounded before:text-sm before:whitespace-nowrap before:z-20 before:pointer-events-none'
        }
    },
    // Site Banners admin page
    siteBanners: {
        page: `min-h-full p-6 ${themeColors.background.DEFAULT} ${themeColors.text.primary}`,
        container: 'mx-auto max-w-6xl space-y-6',
        title: 'text-2xl font-bold',
        subtitle: `mt-1 text-sm ${themeColors.text.secondary}`,
        sectionHeading: 'mb-3 text-xl font-bold',
        loading: `text-sm ${themeColors.text.secondary}`,
        errorBanner: `rounded border ${themeColors.status.error.border} ${themeColors.status.error.bg} px-4 py-3 text-sm ${themeColors.status.error.text}`,
        form: 'grid gap-4 rounded border border-border-light p-4',
        gridFour: 'grid gap-4 md:grid-cols-4',
        gridThree: 'grid gap-4 md:grid-cols-3',
        gridTwo: 'grid gap-4 md:grid-cols-2',
        labelStandalone: 'block text-sm font-semibold',
        labelInline: 'text-sm font-semibold',
        labelWide: 'text-sm font-semibold md:col-span-2',
        field: 'mt-1 block w-full rounded border border-border-light bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary',
        codeField: 'mt-1 block w-full rounded border border-border-light bg-background px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-4 focus:ring-primary/15 focus:border-primary',
        checkboxRow: 'flex flex-wrap gap-4 text-sm font-semibold',
        checkboxLabel: 'flex items-center gap-2',
        routePatternsFieldset: 'rounded border border-border-light p-3 text-sm font-semibold',
        routePatternsGrid: 'mt-2 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 font-normal',
        buttonRow: 'flex gap-3',
        primaryButton: `rounded ${themeColors.primary.DEFAULT} px-4 py-2 text-sm font-bold`,
        secondaryButton: 'rounded border border-border-light bg-background text-foreground hover:bg-background-light px-4 py-2 text-sm font-bold',
        tableContainer: 'overflow-auto rounded border border-border-light',
        table: 'min-w-full text-left text-sm',
        tableHead: 'bg-surface-secondary text-foreground',
        tableHeadCell: 'px-3 py-2',
        tableRow: 'border-t border-border-light',
        tableCell: 'px-3 py-2',
        tableActionCell: 'flex gap-2 px-3 py-2',
        rowEditButton: 'rounded border border-border-light bg-background text-foreground hover:bg-background-light px-3 py-1',
        rowDeleteButton: `rounded border ${themeColors.status.error.border} ${themeColors.status.error.text} hover:${themeColors.status.error.bg} px-3 py-1`,
    },

    // Tags component configuration for item types
    tags: {
        itemTypes: {
            MINIFIG: {
                background: 'bg-yellow-100 dark:bg-yellow-900/30',
                text: 'text-yellow-800 dark:text-yellow-200',
                border: 'border-yellow-300 dark:border-yellow-700'
            },
            PART: {
                background: 'bg-blue-100 dark:bg-blue-900/30',
                text: 'text-blue-800 dark:text-blue-200',
                border: 'border-blue-300 dark:border-blue-700'
            },
            SET: {
                background: 'bg-green-100 dark:bg-green-900/30',
                text: 'text-green-800 dark:text-green-200',
                border: 'border-green-300 dark:border-green-700'
            },
            BOOK: {
                background: 'bg-purple-100 dark:bg-purple-900/30',
                text: 'text-purple-800 dark:text-purple-200',
                border: 'border-purple-300 dark:border-purple-700'
            },
            GEAR: {
                background: 'bg-orange-100 dark:bg-orange-900/30',
                text: 'text-orange-800 dark:text-orange-200',
                border: 'border-orange-300 dark:border-orange-700'
            },
            CATALOG: {
                background: 'bg-pink-100 dark:bg-pink-900/30',
                text: 'text-pink-800 dark:text-pink-200',
                border: 'border-pink-300 dark:border-pink-700'
            },
            INSTRUCTION: {
                background: 'bg-indigo-100 dark:bg-indigo-900/30',
                text: 'text-indigo-800 dark:text-indigo-200',
                border: 'border-indigo-300 dark:border-indigo-700'
            },
            UNSORTED_LOT: {
                background: 'bg-gray-100 dark:bg-gray-700',
                text: 'text-gray-800 dark:text-gray-200',
                border: 'border-gray-300 dark:border-gray-600'
            },
            ORIGINAL_BOX: {
                background: 'bg-red-100 dark:bg-red-900/30',
                text: 'text-red-800 dark:text-red-200',
                border: 'border-red-300 dark:border-red-700'
            }
        },
        generic: {
            background: 'bg-slate-100 dark:bg-slate-700',
            text: 'text-slate-700 dark:text-slate-200',
            border: 'border-slate-300 dark:border-slate-600'
        },
        base: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border',
        removeButton: {
            base: 'ml-1 text-current hover:text-red-600 dark:hover:text-red-400 focus:outline-none',
            icon: 'w-3 h-3'
        },
        addButton: {
            base: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400',
            default: 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600',
            icon: 'w-3 h-3'
        },
        dropdown: {
            backdrop: 'fixed inset-0',
            container: 'fixed bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto min-w-[150px]',
            allOption: {
                base: 'w-full px-3 py-2 text-xs text-left font-semibold block border-b focus:outline-none',
                default: 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border-gray-200 dark:border-gray-700'
            },
            option: {
                base: 'w-full px-3 py-2 text-xs text-left block focus:outline-none',
                default: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700'
            }
        },
        messages: {
            allAssigned: 'text-xs text-gray-500 dark:text-gray-400 italic',
            noTypes: 'text-xs text-gray-500 dark:text-gray-400 italic'
        }
    },
};

export { themeColors, components };
